import { useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';

/**
 * Custom hook to get and set URL parameter values
 * @param {string} paramName - The name of the URL parameter to retrieve/set
 * @param {Array} allowedValues - Array of allowed values for validation (optional)
 * @returns {Array} - [getter function, setter function]
 */
export const useURLParam = (paramName, allowedValues = null) => {
  const location = useLocation();
  const history = useHistory();

  const getParamFromURL = useCallback(() => {
    const urlParams = new URLSearchParams(location.search);
    const paramValue = urlParams.get(paramName);

    if (paramValue && allowedValues && allowedValues.includes(paramValue)) {
      return paramValue;
    }

    if (paramValue && !allowedValues) {
      return paramValue;
    }

    return null;
  }, [location.search, paramName, allowedValues]);

  const setParamInURL = useCallback(
    value => {
      if (allowedValues && !allowedValues.includes(value)) {
        return;
      }

      const urlParams = new URLSearchParams(location.search);
      if (value) {
        urlParams.set(paramName, value);
      } else {
        urlParams.delete(paramName);
      }
      const newPath = `${location.pathname}?${urlParams.toString()}`;
      history.replace(newPath);
    },
    [location.search, location.pathname, history, paramName, allowedValues],
  );

  return [getParamFromURL, setParamInURL];
};
